import type { BaseCommissionType, KickerBonusType } from '@pay-plan/shared';

interface KickerInput {
  bonusType: KickerBonusType;
  bonusPercent?: number;
  bonusAmount?: number;
  met: boolean;
}

export interface PayCalculation {
  totalAdjustedGross: number;
  baseCommissionEarned: number;
  kickerAmounts: number[];
  totalKickersEarned: number;
  totalCommissionEarned: number;
  /**
   * All pay classified as commission rather than salary: commission earned,
   * plus the overtime premium it generates and the discretionary bonus, less
   * the draw. Draw and discretionary bonus are commission for payroll
   * classification purposes, so they group here rather than beside salary.
   *
   * Distinct from totalCommissionEarned, which is commission earned off gross
   * and stays the regular-rate basis for the overtime coefficient.
   */
  totalCommissionPay: number;
  monthlySalary: number;
  discretionaryBonus: number;
  draw: number;
  overtimeCoefficientWeekly: number[];
  overtimeCoefficientTotal: number;
  totalCompensation: number;
  effectiveRate: number;
}

/**
 * Maine overtime coefficient — mirrors computeOvertimeCoefficient in
 * backend/src/lib/calculations.ts (the save-time source of truth). Hourly
 * employees who also earn bonus/commission are owed a half-time premium per
 * overtime week: (hours − 40) / (hours × 2) × (monthlyBonus ÷ 4). Weeks at
 * or under 40 hours contribute nothing.
 */
export function computeOvertimeCoefficient(
  weeklyHours: number[],
  monthlyBonus: number,
): { weekly: number[]; total: number } {
  const weeklyBonusShare = monthlyBonus / 4;
  const weekly = weeklyHours.map((hours) =>
    hours > 40 ? ((hours - 40) / (hours * 2)) * weeklyBonusShare : 0,
  );
  return { weekly, total: weekly.reduce((sum, w) => sum + w, 0) };
}

export interface CalculatePayInput {
  gross1: number;
  gross2: number;
  baseCommissionRate: number;
  /** Gross2 rate on dual-rate plans; undefined = gross2 pays baseCommissionRate. */
  baseCommissionRate2?: number;
  weeklySalary: number;
  kickers: KickerInput[];
  discretionaryBonus?: number;
  draw?: number;
  baseCommissionType?: BaseCommissionType;
  baseCommissionAmount?: number;
  weeklyHours?: number[];
}

// Mirrors computePaySheetTotals in backend/src/lib/calculations.ts (the
// save-time source of truth) — keep the two in sync. totalCommissionPay is the
// one exception: a presentational grouping of stored fields, so it has no
// backend counterpart and changes no persisted number.
export function calculatePay(input: CalculatePayInput): PayCalculation {
  const {
    gross1,
    gross2,
    baseCommissionRate,
    baseCommissionRate2,
    weeklySalary,
    kickers,
    discretionaryBonus = 0,
    draw = 0,
    baseCommissionType = 'percent',
    baseCommissionAmount = 0,
    weeklyHours = [],
  } = input;
  const totalAdjustedGross = gross1 + gross2;

  // Dual path only when a distinct gross2 rate exists — equal/absent rate2
  // keeps the original single-rate expression so displayed totals match
  // historical saves bit-identically.
  const dualRate =
    baseCommissionType === 'percent' &&
    baseCommissionRate2 !== undefined &&
    baseCommissionRate2 !== baseCommissionRate;
  const baseCommissionEarned =
    baseCommissionType === 'flat'
      ? baseCommissionAmount
      : dualRate
        ? gross1 * (baseCommissionRate / 100) + gross2 * ((baseCommissionRate2 ?? 0) / 100)
        : totalAdjustedGross * (baseCommissionRate / 100);

  const kickerAmounts = kickers.map((k) => {
    if (!k.met) return 0;
    if (k.bonusType === 'flat') return k.bonusAmount ?? 0;
    return totalAdjustedGross * ((k.bonusPercent ?? 0) / 100);
  });
  const totalKickersEarned = kickerAmounts.reduce((sum, a) => sum + a, 0);

  const totalCommissionEarned = baseCommissionEarned + totalKickersEarned;
  const monthlySalary = weeklySalary * 4.33;
  const overtime = computeOvertimeCoefficient(weeklyHours, totalCommissionEarned);
  const totalCommissionPay = totalCommissionEarned + overtime.total + discretionaryBonus - draw;
  const totalCompensation = totalCommissionPay + monthlySalary;

  // Effective rate tracks percent-based compensation only — flat base and
  // flat kickers are dollar amounts and do not change the rate.
  const earnedKickerPercent = kickers.reduce(
    (sum, k) => sum + (k.met && k.bonusType === 'percent' ? (k.bonusPercent ?? 0) : 0),
    0,
  );
  // Dual-rate plans report the blended base rate actually earned.
  const baseContribution =
    baseCommissionType !== 'percent'
      ? 0
      : dualRate && totalAdjustedGross > 0
        ? (baseCommissionEarned / totalAdjustedGross) * 100
        : baseCommissionRate;
  const effectiveRate = baseContribution + earnedKickerPercent;

  return {
    totalAdjustedGross,
    baseCommissionEarned,
    kickerAmounts,
    totalKickersEarned,
    totalCommissionEarned,
    totalCommissionPay,
    monthlySalary,
    discretionaryBonus,
    draw,
    overtimeCoefficientWeekly: overtime.weekly,
    overtimeCoefficientTotal: overtime.total,
    totalCompensation,
    effectiveRate,
  };
}

// A blank Gross Label 2 means the template only uses one gross input, so the
// Gross 2 box is hidden everywhere — unless a nonzero value was already saved
// (a historical amount must never be invisible while still counting in totals).
export function showGross2(grossLabel2: string, gross2: number): boolean {
  return grossLabel2.trim() !== '' || gross2 !== 0;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}
